import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, List, ListItem, ListItemText,
  MenuItem, Skeleton, Snackbar, Stack, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BsPencil, BsPlusLg, BsTrash } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import Fiche from '../Expert/Fiche';
import { themeExpert } from '../../theme/expert';

const TYPES = [
  { code: 'description', libelle: 'Texte' },
  { code: 'at_date', libelle: 'Date' },
  { code: 'check_box', libelle: 'Oui / non' },
];

const libelleType = (code) =>
  TYPES.find((t) => t.code === code)?.libelle || code;

/** La trame d'un rapport : ses champs, dans l'ordre où on les remplira. */
const ReportModelView = () => {
  const [modeles, setModeles] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);

  const [fiche, setFiche] = useState(null);
  const [champs, setChamps] = useState([]);
  const [nouveauChamp, setNouveauChamp] = useState(
    { title: '', type_field: 'description' });
  const [aSupprimer, setASupprimer] = useState(null);
  const [champASupprimer, setChampASupprimer] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axiosInstance.get('/report_model/');
      setModeles(data.results || data);
      setErreur('');
    } catch {
      setErreur("Les modèles n'ont pas pu être chargés.");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const chargerChamps = async (modeleId) => {
    const { data } = await axiosInstance.get('/reportfieldtexts/',
                                             { params: { report_id: modeleId } });
    setChamps(data.results || data);
  };

  const ouvrir = async (modele) => {
    setNouveauChamp({ title: '', type_field: 'description' });
    setChamps([]);
    setFiche(modele ? { ...modele } : { name: '', actif: true });
    if (modele) {
      try {
        await chargerChamps(modele.id);
      } catch {
        setErreur("Les champs de ce modèle n'ont pas pu être chargés.");
      }
    }
  };

  const enregistrer = async (e) => {
    e.preventDefault();
    setOccupe(true);
    try {
      const corps = { name: fiche.name.trim(), actif: fiche.actif };
      if (fiche.id) {
        await axiosInstance.put(`/report_model/${fiche.id}/`, corps);
        setMessage('Modèle enregistré');
        setFiche(null);
      } else {
        const { data } = await axiosInstance.post('/report_model/', corps);
        // On reste dans la fenêtre : un modèle sans champ ne sert à rien,
        // et fermer obligerait à le rouvrir aussitôt pour les ajouter.
        setFiche(data);
        setMessage('Modèle créé — ajoutez ses champs.');
      }
      charger();
    } catch {
      setErreur("Le modèle n'a pas pu être enregistré.");
    } finally {
      setOccupe(false);
    }
  };

  const ajouterChamp = async () => {
    setOccupe(true);
    try {
      const { data } = await axiosInstance.post('/reportfieldtexts/', {
        ...nouveauChamp, title: nouveauChamp.title.trim(), report: fiche.id,
      });
      setChamps((etat) => [...etat, data]);
      setNouveauChamp({ title: '', type_field: 'description' });
    } catch {
      setErreur("Le champ n'a pas pu être ajouté.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimerChamp = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/reportfieldtexts/${champASupprimer.id}/`);
      setChamps((etat) => etat.filter((c) => c.id !== champASupprimer.id));
      setChampASupprimer(null);
    } catch {
      setErreur("Le champ n'a pas pu être retiré.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/report_model/${aSupprimer.id}/`);
      setASupprimer(null);
      setMessage('Modèle supprimé');
      charger();
    } catch {
      setErreur("Le modèle n'a pas pu être supprimé.");
    } finally {
      setOccupe(false);
    }
  };

  return (
    <>
      <Fiche
        titre="Modèles de rapport"
        explication="Depuis un dossier, partir d'un modèle y ajoute tous ses champs d'un coup."
        actions={(
          <Button size="small" variant="contained" startIcon={<BsPlusLg />}
                  onClick={() => ouvrir(null)}>
            Nouveau modèle
          </Button>
        )}
        sx={{ p: 0 }}
      >
        {erreur && <Alert severity="error" sx={{ m: 2 }}
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        {chargement && (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[...Array(3)].map((_, i) => <Skeleton key={i} height={36} />)}
          </Stack>
        )}

        {!chargement && modeles.length === 0 && (
          <Typography variant="body2" color="text.secondary"
                      sx={{ py: 4, textAlign: 'center' }}>
            Aucun modèle pour l'instant.
          </Typography>
        )}

        {!chargement && modeles.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Modèle</TableCell>
                  <TableCell align="right">&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modeles.map((modele) => (
                  <TableRow key={modele.id} hover
                            sx={{ opacity: modele.actif === false ? 0.5 : 1 }}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center"
                             flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {modele.name}
                        </Typography>
                        {modele.actif === false && (
                          <Chip size="small" label="inactif" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => ouvrir(modele)}>
                          <BsPencil />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small"
                                    onClick={() => setASupprimer(modele)}>
                          <BsTrash />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Fiche>

      <ThemeProvider theme={themeExpert}>
        <Dialog open={fiche !== null} onClose={() => setFiche(null)}
                fullWidth maxWidth="sm">
          <DialogTitle>
            {fiche?.id ? 'Modifier le modèle' : 'Nouveau modèle de rapport'}
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={3}>
              <Box component="form" onSubmit={enregistrer} noValidate>
                <Stack spacing={2.5}>
                  <TextField label="Nom du modèle" value={fiche?.name || ''}
                             onChange={(e) => setFiche(
                               (s) => ({ ...s, name: e.target.value }))}
                             required autoFocus
                             helperText="Ex. : rapport de dégât des eaux." />
                  <Stack direction="row" spacing={2} alignItems="center"
                         justifyContent="space-between">
                    <FormControlLabel
                      control={<Switch checked={Boolean(fiche?.actif)}
                                       onChange={(e) => setFiche(
                                         (s) => ({ ...s, actif: e.target.checked }))} />}
                      label="Proposé aux dossiers" />
                    <Button type="submit" variant="contained" size="small"
                            disabled={occupe || !fiche?.name?.trim()}>
                      {fiche?.id ? 'Enregistrer' : 'Créer'}
                    </Button>
                  </Stack>
                </Stack>
              </Box>

              {fiche?.id && (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Champs du modèle{champs.length > 0 && ` (${champs.length})`}
                  </Typography>

                  {champs.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Aucun champ. Un modèle sans champ n'ajoute rien au
                      dossier.
                    </Alert>
                  ) : (
                    <List dense sx={{ border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 1, mb: 2 }}>
                      {champs.map((champ) => (
                        <ListItem
                          key={champ.id}
                          secondaryAction={(
                            /* Les champs s'ajoutaient sans jamais pouvoir
                               être retirés. */
                            <IconButton size="small" aria-label="Retirer"
                                        onClick={() => setChampASupprimer(champ)}>
                              <BsTrash />
                            </IconButton>
                          )}
                        >
                          <ListItemText primary={champ.title}
                                        secondary={libelleType(champ.type_field)} />
                        </ListItem>
                      ))}
                    </List>
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                         alignItems={{ sm: 'flex-start' }}>
                    <TextField label="Nouveau champ"
                               value={nouveauChamp.title}
                               onChange={(e) => setNouveauChamp(
                                 (s) => ({ ...s, title: e.target.value }))} />
                    <TextField select label="Type"
                               value={nouveauChamp.type_field}
                               onChange={(e) => setNouveauChamp(
                                 (s) => ({ ...s, type_field: e.target.value }))}
                               sx={{ minWidth: 150 }}>
                      {TYPES.map(({ code, libelle }) => (
                        <MenuItem key={code} value={code}>{libelle}</MenuItem>
                      ))}
                    </TextField>
                    <Button variant="outlined" onClick={ajouterChamp}
                            disabled={occupe || !nouveauChamp.title.trim()}
                            sx={{ mt: { sm: 0.5 }, flexShrink: 0 }}>
                      Ajouter
                    </Button>
                  </Stack>
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setFiche(null)}>Fermer</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={champASupprimer !== null}
                onClose={() => setChampASupprimer(null)}>
          <DialogTitle>Retirer ce champ du modèle ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {champASupprimer?.title} » ne sera plus ajouté aux nouveaux
              rapports. Les rapports déjà construits ne bougent pas.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setChampASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimerChamp} color="error" variant="contained"
                    disabled={occupe}>
              Retirer
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={aSupprimer !== null} onClose={() => setASupprimer(null)}>
          <DialogTitle>Supprimer ce modèle ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {aSupprimer?.name} » et ses champs seront perdus. Les rapports
              déjà construits à partir de lui ne bougent pas.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimer} color="error" variant="contained"
                    disabled={occupe}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(message)} autoHideDuration={3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
};

export default ReportModelView;
